import { dismissHint, hintDismissed, prefs, setPrefs } from '../state/prefs.js';
import { el, on } from './dom.js';

export interface Hint {
  id: string;
  icon: string;
  /** Plain text with **bold** and `key` markup. */
  text: string;
  /** Lower numbers win when several hints are eligible at once. */
  priority: number;
  /** Auto-retire the hint once the player has clearly done the thing. */
  retireAfter?: number;
}

/**
 * The coach bar.
 *
 * Four rules, taken from the brief and from what went wrong with the first
 * version of it:
 *
 * 1. It never needs dismissing to carry on.
 * 2. It **never sits over the board.** It used to float above the bottom of
 *    the board, which is exactly where a player builds, so it covered their
 *    own letters. It is now docked between the board and the rack, in the
 *    normal flow, where it cannot overlap anything.
 * 3. Touching anything at all puts the current hint away. Hunting for a small
 *    × to get on with the game is the opposite of unobtrusive.
 * 4. One tap silences the lot. The × only ever silenced the hint it was
 *    attached to, so somebody who did not want tips had to dismiss them one at
 *    a time; there is now a "no more tips" beside it.
 *
 * Everything shown is contextual: a hint appears because the board is in the
 * state it describes, and goes when it is not.
 */
/** How long the bar stays quiet after being waved away. */
const QUIET_MS = 45_000;

export class Coach {
  readonly root: HTMLElement;
  #icon: HTMLElement;
  #text: HTMLElement;
  #close: HTMLButtonElement;
  #never: HTMLButtonElement;
  #current: Hint | null = null;
  #shownAt = 0;
  #seenCount = new Map<string, number>();
  /**
   * Tips put away this session by touching something else.
   *
   * Hiding one was not enough: the very touch that dismissed it usually
   * changed the board — a tile placed, a selection made — the board
   * re-rendered, and the same tip was offered straight back. On a phone that
   * read as a tip that could not be dismissed at all.
   */
  #putAway = new Set<string>();
  #detach: (() => void) | null = null;

  constructor() {
    this.#icon = el('span', { class: 'coach__icon' });
    this.#text = el('span', { class: 'coach__text' });
    this.#never = el('button', {
      class: 'coach__never', type: 'button', text: 'Hide all tips',
      title: 'Stop showing tips altogether. Settings can bring them back.',
    });
    on(this.#never, 'click', (e) => {
      e.stopPropagation();
      setPrefs({ hintsEnabled: false });
      this.hide();
    });
    this.#close = el('button', {
      class: 'coach__close', type: 'button', 'aria-label': 'Do not show this hint again',
      title: 'Do not show this particular tip again',
      text: '×',
    });
    on(this.#close, 'click', (e) => {
      e.stopPropagation();
      if (this.#current) { dismissHint(this.#current.id); this.hide(); }
    });
    this.root = el('div', {
      class: 'coach', 'data-show': 'false', role: 'note', 'aria-live': 'polite',
    }, this.#icon, this.#text, this.#never, this.#close);
  }

  /**
   * Put the current hint away as soon as the player does anything.
   *
   * Capture phase, and passive: this must never interfere with the gesture
   * that triggered it — the tile still gets picked up, the button still gets
   * pressed. A short grace period stops the very tap that *revealed* a hint
   * from dismissing it in the same breath.
   */
  watchForActivity(): void {
    if (this.#detach) return;
    /*
     * The bar sits in the flow, so showing or hiding it changes the board's
     * height. Doing that in the middle of a drag moves the board under the
     * finger. So a touch *marks* the tip as put away, and the bar actually
     * collapses when the finger lifts; nor is a new tip shown while a finger
     * is down.
     */
    const down = (e: Event): void => {
      this.#pressing = true;
      if (!this.#current) return;
      // A touch on the tip's own buttons is handled by those buttons.
      if (e.target instanceof Node && this.root.contains(e.target)) return;
      if (performance.now() - this.#shownAt < 600) return;
      this.#putAway.add(this.#current.id);
      this.#quietUntil = performance.now() + QUIET_MS;
      this.#hideWhenReleased = true;
    };
    const up = (): void => {
      this.#pressing = false;
      if (this.#hideWhenReleased) { this.#hideWhenReleased = false; this.hide(); }
    };
    const key = (): void => {
      if (!this.#current || performance.now() - this.#shownAt < 600) return;
      this.#putAway.add(this.#current.id);
      this.#quietUntil = performance.now() + QUIET_MS;
      this.hide();
    };
    const opts = { capture: true, passive: true } as AddEventListenerOptions;
    document.addEventListener('pointerdown', down, opts);
    document.addEventListener('pointerup', up, opts);
    document.addEventListener('pointercancel', up, opts);
    document.addEventListener('keydown', key, opts);
    this.#detach = () => {
      document.removeEventListener('pointerdown', down, opts);
      document.removeEventListener('pointerup', up, opts);
      document.removeEventListener('pointercancel', up, opts);
      document.removeEventListener('keydown', key, opts);
      this.#detach = null;
    };
  }

  #pressing = false;
  #hideWhenReleased = false;
  /**
   * After a tip is put away by touching something else, nothing new appears
   * for a while. Otherwise the next redraw offered the *next* tip in the
   * queue, and to somebody who had just swiped one away that was
   * indistinguishable from the tip refusing to go.
   */
  #quietUntil = 0;

  destroy(): void { this.#detach?.(); }

  /** Show the highest-priority eligible hint, or nothing. */
  offer(candidates: Hint[]): void {
    if (!prefs().hintsEnabled) { this.hide(); return; }
    const eligible = candidates
      .filter((h) => !hintDismissed(h.id))
      .filter((h) => !this.#putAway.has(h.id))
      // Three sightings and a hint has said its piece. Without a ceiling the
      // same tip came back every time the board re-entered the state that
      // triggers it, which is what made them feel like pop-ups.
      .filter((h) => (this.#seenCount.get(h.id) ?? 0) < (h.retireAfter ?? 3))
      .sort((a, b) => a.priority - b.priority);
    const next = eligible[0] ?? null;

    // Nothing changes size while a finger is on the screen.
    if (this.#pressing) return;
    if (!this.#current && performance.now() < this.#quietUntil) return;
    if (!next) { this.hide(); return; }
    if (this.#current?.id === next.id) return;
    // Do not swap a hint the player may be mid-read of.
    if (this.#current && performance.now() - this.#shownAt < 1400) return;

    this.#current = next;
    this.#shownAt = performance.now();
    this.#seenCount.set(next.id, (this.#seenCount.get(next.id) ?? 0) + 1);
    this.#icon.textContent = next.icon;
    this.#text.replaceChildren(...markup(next.text));
    this.root.dataset.show = 'true';
  }

  hide(): void {
    // Remembered as seen, so putting it away by carrying on with the game is
    // enough to stop it reappearing the moment the board changes again.
    if (this.#current) {
      const id = this.#current.id;
      this.#seenCount.set(id, (this.#seenCount.get(id) ?? 0) + 1);
    }
    this.#current = null;
    this.root.dataset.show = 'false';
  }
}

/** `**bold**` and `` `key` `` become elements; everything else stays text. */
function markup(src: string): Node[] {
  const out: Node[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  for (const m of src.matchAll(re)) {
    const i = m.index ?? 0;
    if (i > last) out.push(document.createTextNode(src.slice(last, i)));
    const tok = m[0];
    if (tok.startsWith('**')) out.push(el('b', { text: tok.slice(2, -2) }));
    else out.push(el('kbd', { class: 'coach__key', text: tok.slice(1, -1) }));
    last = i + tok.length;
  }
  if (last < src.length) out.push(document.createTextNode(src.slice(last)));
  return out;
}

/** The catalogue. Conditions are evaluated by the app each render. */
export const HINTS = {
  firstTile: {
    id: 'first-tile', icon: '👆', priority: 10,
    text: 'Drag a letter up from your rack, or tap a square then tap letters.',
  },
  connect: {
    id: 'connect', icon: '🔗', priority: 20,
    text: 'Every word must touch the others — build out from a shared letter.',
  },
  adjacency: {
    id: 'adjacency', icon: '⚠️', priority: 5,
    text: 'Touching words read as one. **CAR** next to **MUCH** becomes CARMUCH — leave a gap.',
  },
  panBoard: {
    id: 'pan-board', icon: '🖐️', priority: 38,
    text: 'Drag the background to move the board about. Two fingers to zoom.',
  },
  longPress: {
    id: 'long-press', icon: '✋', priority: 40,
    text: 'Hold a letter to pick it out. Hold a few, then drag any one of them and the whole set moves.',
  },
  doubleTap: {
    id: 'double-tap', icon: '👆', priority: 42,
    text: 'Double-tap does the same as holding — whichever is easier. A tap on bare board puts them all down.',
  },
  marquee: {
    id: 'lasso', icon: '➰', priority: 43,
    text: 'Hold empty board for a second, then **draw round** the letters you want — any shape. Drag one and they all move.',
  },
  nudge: {
    id: 'nudge', icon: '⌨️', priority: 45,
    text: 'With tiles selected, `Ctrl` + arrows nudges them a square at a time.',
  },
  direction: {
    id: 'direction', icon: '↔️', priority: 50,
    text: 'Tap a letter on the grid to build a word through it — the cursor jumps just past it, the free way.',
  },
  readyToCall: {
    id: 'ready-call', icon: '🎉', priority: 1,
    text: 'Rack empty and every word checks out — **call it**.',
  },
  checkBudget: {
    id: 'check-budget', icon: '🔍', priority: 30,
    text: 'Not sure about a word? Spend a **check** — but you only get a few.',
  },
  checkWindow: {
    id: 'check-window', icon: '⏱️', priority: 25,
    text: 'Checks expire shortly after you move a tile, so check while it is fresh.',
  },
  dump: {
    id: 'dump', icon: '♻️', priority: 55,
    text: 'Stuck with an awkward letter? Hold it in your rack to trade it in.',
  },
  bagLow: {
    id: 'bag-low', icon: '🏁', priority: 2,
    text: 'Last letters in the bag — finish first and you win the match.',
  },
  stuck: {
    id: 'stuck', icon: '✋', priority: 65,
    text: 'Nothing playable? Say you are **stuck**. If everyone agrees, the table draws.',
  },
  stuckWaiting: {
    id: 'stuck-waiting', icon: '⏳', priority: 8,
    text: 'Somebody has said they are stuck — the table draws once everyone agrees.',
  },
  variableDraw: {
    id: 'variable-draw', icon: '🔢', priority: 70,
    text: 'The number in the call is how many letters everyone draws — the host sets it.',
  },
  spectate: {
    id: 'spectate', icon: '👀', priority: 60,
    text: 'Open the players panel and tap somebody to watch their grid as they build it.',
  },
  crossings: {
    id: 'crossings', icon: '✖️', priority: 58,
    text: 'A letter where two words cross is paid **twice** — once across, once down.',
  },
  unusedCost: {
    id: 'unused-cost', icon: '➖', priority: 57,
    text: 'Letters you never use come off your score, whether they are in your hand or stranded on the board.',
  },
  teamUp: {
    id: 'team-up', icon: '🤝', priority: 15,
    text: 'Join someone else’s seat to play as a team on one shared rack.',
  },
} satisfies Record<string, Hint>;
